import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Bouton, Carte, EnTetePage, GroupeBoutons, Message, Vide,
  Dialogue, IconeEleves, IconeTelecharger, SqueletteTableau,
} from '@journal/ui';
import { ControlesEvaluation, StatistiquesControles } from './ControlesEvaluation.tsx';
import { useSession } from '../lib/session.tsx';
import { useDonnees } from '../lib/donnees.ts';
import { useAnneeActive } from '../lib/annee.tsx';
import { Eleves, type Eleve } from './Eleves.tsx';
import { useImpression, useTelechargementPdf } from '../lib/impression.tsx';
import { demandeV1 } from '../lib/impression.ts';
import { evaluationV1, STYLE_STATS_V1, titreV1, type GrilleV1 } from '../lib/gabarits-v1.ts';

/**
 * Évaluation par compétences.
 *
 * ⚠ Données d'évaluation de mineurs. Elles ne sortent jamais de l'écran : ni
 * export automatique, ni envoi par courriel, ni journalisation. Le commentaire
 * est rédigé dans les logs côté serveur.
 *
 * ── Les quatre niveaux ne sont PAS une note ─────────────────────────────────
 * Bleu, orange, jaune, vert : c'est l'échelle du livret scolaire français. La
 * tentation de les afficher comme 1/2/3/4 ou de calculer une moyenne visible
 * par élève est à refuser — l'évaluation par compétences existe précisément
 * pour ne pas produire de moyenne. La synthèse par domaine, elle, agrège pour
 * repérer un domaine fragile, ce qui est un autre usage.
 *
 * ── La couleur ne porte jamais seule ────────────────────────────────────────
 * Chaque niveau est doublé de son libellé. Un bulletin lu par un parent
 * daltonien, ou une capture en noir et blanc, doivent rester compréhensibles.
 */


interface Evaluation {
  id: string;
  studentId: string;
  curriculumObjectiveId: string | null;
  tenantObjectiveId: string | null;
  level: string;
  comment: string;
  ratedOn: string;
}

interface ObjectifProgramme {
  id: string;
  text: string;
  position: number;
  source: 'programme' | 'client';
}

interface SousDomaineProgramme {
  id: string;
  name: string;
  position: number;
  objectives: ObjectifProgramme[];
}

interface DomaineProgramme {
  id: string;
  name: string;
  subject: string;
  level: string;
  color: string;
  position: number;
  subdomains: SousDomaineProgramme[];
}

/**
 * `/evaluation?classe=ouverte` ouvre directement la gestion de la classe.
 *
 * C'est l'adresse du raccourci « Élèves » de l'accueil : la liste de classe
 * n'a pas d'écran à elle, elle vit ici.
 */
export const PARAMETRE_CLASSE = 'classe';

export function Evaluation() {
  const annee = useAnneeActive();

  const eleves = useDonnees<Eleve[]>('/students');
  // Le programme de la CLASSE, toutes versions fusionnées : la grille en a
  // besoin pour connaître ses matières, et une classe à cheval sur deux cycles
  // n'en voyait qu'un.
  const arbre = useDonnees<DomaineProgramme[]>('/curriculum/tree');

  /*
   * ── Le module se réduit aux CONTRÔLES ─────────────────────────────────────
   *
   * C'est le mouvement de la v1, arrivé après plusieurs essais : un enseignant
   * n'évalue pas l'objectif « écrire sans erreur les mots mémorisés », il
   * évalue une dictée. La notation objectif par objectif restait juste sur le
   * papier et ne correspondait à aucun geste réel — d'où une grille immense
   * que personne ne remplissait.
   *
   * Les données de l'ancienne notation ne sont PAS supprimées : la table
   * `student_ratings` et ses routes restent en place, exactement comme la v1
   * a conservé `notes_objectifs`. On ne détruit pas des évaluations de mineurs
   * pour se simplifier la vie, et rien n'interdit d'y revenir.
   */
  const [vue, setVue] = useState<'controles' | 'stats'>('controles');

  /*
   * Gestion de la classe, en dialogue depuis cet écran.
   *
   * C'est sa place dans l'application d'origine (`gererEleves`). En faire un
   * module de la barre latérale était une invention : on ne consulte sa liste
   * d'appel que pour évaluer, jamais pour elle-même.
   */
  const [parametres, setParametres] = useSearchParams();
  // Change à chaque modification de la liste de la classe : le tableau des
  // contrôles et les statistiques se rechargent (il fallait rafraîchir la page).
  const [version, setVersion] = useState(0);
  const [classeOuverte, setClasseOuverteBrut] = useState(
    () => parametres.get(PARAMETRE_CLASSE) === 'ouverte',
  );
  /*
   * Fermer retire aussi le paramètre de l'adresse : sans cela, recharger la
   * page — ou revenir en arrière — rouvrirait la gestion de la classe.
   */
  function setClasseOuverte(ouverte: boolean) {
    setClasseOuverteBrut(ouverte);
    if (!ouverte && parametres.has(PARAMETRE_CLASSE)) {
      const suite = new URLSearchParams(parametres);
      suite.delete(PARAMETRE_CLASSE);
      setParametres(suite, { replace: true });
    }
  }

  /*
   * Un tableau par domaine, plus les statistiques : le sélecteur les prend
   * TOUS, dans l'ordre de la page. Une feuille par domaine serait plus lourde
   * à manipuler qu'un document unique qu'on parcourt.
   */
  const statistiques = useImpression({
    selecteur: '[data-impression="evaluation"]',
    // Comme la v1 : « Évaluation — statistiques — 2026-2027 ».
    titre: titreV1('Évaluation — statistiques', annee.donnees?.label),
    orientation: 'paysage',
    style: STYLE_STATS_V1,
  });
  /*
   * Les grilles de contrôles, au modèle de la v1 (`lib/gabarits-v1.ts`) : la
   * matière affichée, un tableau par domaine, pastilles pleines.
   */
  const { api } = useSession();
  const pdf = useTelechargementPdf();
  const [matiereAffichee, setMatiereAffichee] = useState('');
  const impression = vue === 'stats' ? statistiques : {
    enCours: pdf.enCours,
    erreur: pdf.erreur,
    lancer: () => {
      if (!matiereAffichee) return;
      void (async () => {
        try {
          const grille = await api.requete<GrilleV1>(`/assessments?subject=${encodeURIComponent(matiereAffichee)}`);
          await pdf.telecharger(demandeV1(evaluationV1(
            titreV1('Évaluation', matiereAffichee, annee.donnees?.label), grille,
          )));
        } catch (e) {
          pdf.signaler(e instanceof Error ? e.message : 'Une erreur est survenue.');
        }
      })();
    },
  };

  if (annee.erreur) {
    return (
      <Carte titre="Évaluation">
        <Vide titre="Aucune année scolaire active">
          Les évaluations suivent les élèves de l'année en cours. Créez une
          année scolaire dans les réglages pour commencer.
        </Vide>
      </Carte>
    );
  }

  if (eleves.chargement && !eleves.donnees) {
    return (
      <div className="flex flex-col gap-5">
        {impression.erreur && <Message ton="erreur">{impression.erreur}</Message>}

      <EnTetePage titre="Évaluation" />
        <Carte><SqueletteTableau lignes={6} colonnes={2} /></Carte>
      </div>
    );
  }
  if (eleves.erreur) return <Message ton="erreur">{eleves.erreur}</Message>;

  if ((eleves.donnees?.length ?? 0) === 0) {
    return (
      <>
        <EnTetePage titre="Évaluation" />
        <Carte>
          {/*
            * L'issue est DANS l'écran vide. La gestion de la classe vit
            * désormais derrière un bouton de l'en-tête, qui n'est pas rendu
            * ici : sans ce relais, une classe vide serait sans issue.
            */}
          <Vide
            icone={<IconeEleves taille={28} />}
            titre="Aucun élève dans cette classe"
            action={
              <Bouton
                variante="primary"
                icone={<IconeEleves taille={16} />}
                onClick={() => setClasseOuverte(true)}
              >
                Gérer la classe
              </Bouton>
            }
          >
            Renseignez votre liste de classe pour commencer à évaluer.
          </Vide>
        </Carte>

        <Dialogue
          titre="Gérer la classe"
          ouvert={classeOuverte}
          surFermeture={() => { setClasseOuverte(false); void eleves.recharger(); }}
          largeur="lg"
          // On y travaille sans rien valider : la sortie est la croix, Échap ou
          // le fond. Sans `fermetureLibre`, ce dialogue n'aurait aucune porte.
          fermetureLibre
        >
          <Eleves enDialogue surChangement={() => { setVersion((v) => v + 1); void eleves.recharger(); }} />
        </Dialogue>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        * Pas de sous-titre.
        *
        * Il nommait l'élève sélectionné, ce qui n'a plus de sens sans la vue
        * par élève. Y mettre l'effectif à la place le ferait apparaître deux
        * fois, puisque le dialogue « Gérer la classe » le porte déjà — et une
        * information affichée deux fois est une information qu'on finit par ne
        * plus lire.
        */}
      <EnTetePage
        titre="Évaluation"
        actions={
          <>
            <GroupeBoutons
              legende="Vue"
              valeur={vue}
              surChangement={setVue}
              options={[
                { valeur: 'controles', texte: 'Contrôles', titre: 'Noter la classe sur un contrôle' },
                { valeur: 'stats', texte: 'Statistiques', titre: 'La classe entière, domaine par domaine' },
              ]}
            />
            <Bouton
              variante="secondary"
              icone={<IconeEleves taille={16} />}
              onClick={() => setClasseOuverte(true)}
            >
              Gérer la classe
            </Bouton>
            <Bouton
              variante="ghost"
              icone={<IconeTelecharger taille={16} />}
              chargement={impression.enCours}
              onClick={impression.lancer}
            >
              Télécharger le PDF
            </Bouton>
          </>
        }
      />

      <Dialogue
        titre="Gérer la classe"
        ouvert={classeOuverte}
        surFermeture={() => {
          setClasseOuverte(false);
          // La liste a pu changer : les tableaux de contrôles en dépendent.
          void eleves.recharger();
        }}
        largeur="lg"
        fermetureLibre
      >
        <Eleves enDialogue surChangement={() => { setVersion((v) => v + 1); void eleves.recharger(); }} />
      </Dialogue>

      {vue === 'controles' && (
        <ControlesEvaluation
          version={version}
          surMatiere={setMatiereAffichee}
          // Les matières viennent du référentiel affiché, pas de la table des
          // matières du client : un contrôle se rattache à un domaine du
          // programme officiel.
          // Tri à la française, comme la Programmation et le Programme : un
          // `.sort()` brut range « Éducation physique » APRÈS « Sciences ».
          matieres={[...new Set((arbre.donnees ?? []).map((d) => d.subject))]
            .sort((a, b) => a.localeCompare(b, 'fr'))}
        />
      )}

      {vue === 'stats' && <StatistiquesControles version={version} />}

    </div>
  );
}

/**
 * L'échelle du livret, dans l'ordre officiel.
 *
 * Une SEULE déclaration, celle du tableau qui la rend. Elle vivait ici en
 * double, avec les mêmes couleurs et des libellés différents : retoucher l'une
 * laissait l'autre en place, et rien ne disait laquelle était affichée. Celle-ci
 * n'était en fait consommée nulle part.
 */
export { NIVEAUX as NIVEAUX_LIVRET } from '../components/TableauControles.tsx';
